// GastroPilot / RestroPilot — scalable i18n dictionary
// Three languages: en (default) · de · tr
// Adding a new language: (1) append its code to `supported`, (2) add its
// translations object below with the same keys, (3) that's it.
// All translation keys are flat and namespaced by comment sections so future
// components have a single, discoverable source of truth.

export const supported = ["en", "de", "tr"];
export const defaultLang = "en";

// Nice-to-have metadata for the selector UI.
export const languageMeta = {
  en: { code: "en", label: "English", nativeLabel: "English", flag: "EN" },
  de: { code: "de", label: "German",  nativeLabel: "Deutsch", flag: "DE" },
  tr: { code: "tr", label: "Turkish", nativeLabel: "Türkçe",  flag: "TR" },
};

/**
 * Detect the browser's preferred language. Returns one of `supported`
 * (falls back to `defaultLang`).
 */
export function detectBrowserLang() {
  if (typeof navigator === "undefined") return defaultLang;
  const candidates = [
    navigator.language,
    ...(navigator.languages || []),
  ].filter(Boolean);
  for (const raw of candidates) {
    const code = String(raw).toLowerCase().split("-")[0];
    if (supported.includes(code)) return code;
  }
  return defaultLang;
}

const en = {
  // ---------- brand ----------
  appName: "RestroPilot",
  tagline: "Finance & business intelligence for your restaurant",
  brandLine1: "For your restaurant",
  brandLine2: "finance & BI.",
  brandDesc: "Cash flow, expenses, invoices, VAT and profit & loss in one place. EN / DE / TR supported.",

  // ---------- common ----------
  save: "Save",
  cancel: "Cancel",
  delete: "Delete",
  edit: "Edit",
  add: "Add",
  close: "Close",
  loading: "Loading…",
  empty: "No records",
  yes: "Yes",
  no: "No",
  search: "Search…",
  logout: "Log out",
  from: "From",
  to: "To",
  date: "Date",
  amount: "Amount",
  category: "Category",
  status: "Status",
  notes: "Notes",
  total: "Total",
  active: "Active",
  actions: "Actions",
  retry: "Retry",
  confirm: "Confirm",
  back: "Back",
  next: "Next",
  submit: "Submit",
  create: "Create",
  update: "Update",
  refresh: "Refresh",
  export: "Export",
  import: "Import",
  print: "Print",
  filter: "Filter",
  clear: "Clear",
  clearAll: "Clear all",
  reset: "Reset",
  copy: "Copy",
  copied: "Copied",
  optional: "Optional",
  required: "Required",
  preview: "Preview",
  today: "Today",
  yesterday: "Yesterday",
  tomorrow: "Tomorrow",
  thisWeek: "This week",
  thisMonth: "This month",
  lastMonth: "Last month",
  last6Months: "Last 6 months",
  thisYear: "This year",
  all: "All",
  none: "None",
  ok: "OK",
  areYouSure: "Are you sure?",
  deleteConfirm: "This record will be permanently deleted.",
  passwordsMismatch: "Passwords do not match.",
  requiredField: "This field is required.",
  minChars: "At least {n} characters",
  invalidEmail: "Please enter a valid email address.",
  saved: "Saved",
  saveFailed: "Save failed",
  loadFailed: "Load failed",
  networkError: "Network error — please try again.",

  // ---------- auth ----------
  login: "Log in",
  register: "Create account",
  email: "Email",
  password: "Password",
  passwordAgain: "Confirm password",
  fullName: "Full name",
  restaurantName: "Restaurant name",
  goToLogin: "Already have an account? Log in",
  goToRegister: "No account yet? Create your restaurant",
  loginHint: "Log in with your email and password",
  forgotPassword: "Forgot password?",
  resetPassword: "Reset password",
  createRestaurant: "Create my restaurant",
  registerHero: "Move your restaurant to RestroPilot",
  registerHeroDesc: "Create your own tenant — nobody else can access your data.",
  registerSubline: "Fill in the details below and launch your restaurant in 30 seconds.",
  restaurantNameHelper: "This name appears as the title of your dashboard.",
  passwordMinPlaceholder: "At least 6 characters",
  passwordConfirmPlaceholder: "Re-enter your password",

  // ---------- nav ----------
  dashboard: "Dashboard",
  finance: "Finance",
  inventory: "Inventory",
  staff: "Staff",
  reports: "Reports",
  settings: "Settings",
  forecast: "Forecast",
  imports: "Imports",
  hq: "HQ",
  more: "More",
  copilot: "Copilot",
  orders: "Orders",
  tables: "Tables",
  menu: "Menu",
  kds: "KDS",
  admin: "Admin",

  // ---------- dashboard BI ----------
  revenueMtd: "Revenue MTD",
  expensesMtd: "Expenses MTD",
  profitMtd: "Profit MTD",
  marginMtd: "Profit margin",
  cashOnHand: "Cash on hand",
  apOpen: "Open payables",
  arOpen: "Open receivables",
  revenueVsExpenses: "Revenue vs Expenses",
  expenseBreakdown: "Expenses by category",
  welcome: "Welcome",
  profitMargin: "Profit margin",
  revenueTrend: "Revenue trend",
  upcomingPayments: "Upcoming payments",
  daysToDue: "Days to due",
  todaysPriorities: "Today's priorities",
  businessHealth: "Business health",
  runway: "Cash runway",
  aiInsight: "AI insight",
  aiInsightHint: "Automatic daily analysis of your operations.",
  regenerate: "Regenerate",
  lastUpdated: "Last updated",
  hoursAgo: "{n} h ago",

  // ---------- finance tabs ----------
  overview: "Overview",
  cashFlow: "Cash flow",
  expenses: "Expenses",
  invoices: "Invoices",
  taxes: "Taxes",
  profitLoss: "P & L",
  bankAccounts: "Bank accounts",
  revenue: "Revenue",

  // ---------- expenses ----------
  newExpense: "New expense",
  vendor: "Vendor",
  bankAccount: "Account",
  taxAmount: "VAT amount",
  description: "Description",
  recurring: "Recurring",
  recurringPeriod: "Recurring period",
  weekly: "Weekly",
  monthly: "Monthly",
  yearly: "Yearly",

  // ---------- invoices ----------
  newInvoice: "New invoice",
  invoiceNumber: "Invoice #",
  partyName: "Counterparty",
  issueDate: "Issue date",
  dueDate: "Due date",
  netAmount: "Net amount",
  taxRate: "VAT rate",
  apInvoices: "Payable (AP)",
  arInvoices: "Receivable (AR)",
  markPaid: "Mark as paid",
  invoiceStatusDraft: "Draft",
  invoiceStatusOpen: "Open",
  invoiceStatusPaid: "Paid",
  invoiceStatusOverdue: "Overdue",

  // ---------- revenue ----------
  newRevenue: "New revenue entry",
  cash: "Cash",
  card: "Card",
  online: "Online / Delivery",
  bankTransfer: "Bank transfer",
  other: "Other",
  revenueDaily: "Daily",
  revenueMonthly: "Monthly",
  revenueCsv: "CSV / Excel import",
  revenueMode: "Revenue tracking mode",

  // ---------- bank accounts ----------
  newAccount: "New account",
  accountName: "Account name",
  iban: "IBAN",
  balance: "Balance",
  isCash: "Cash account",

  // ---------- settings ----------
  brand: "Brand",
  brandName: "Brand name",
  logoUrl: "Logo URL",
  faviconUrl: "Favicon URL",
  primaryColor: "Primary color",
  language: "Language",
  languageEn: "English",
  languageTr: "Turkish",
  languageDe: "German",
  restaurantSettings: "Restaurant settings",
  address: "Address",
  companyInfo: "Company info",
  companyInfoDesc: "Appears on invoices and reports",
  contactSection: "Contact",
  legalTaxSection: "Legal & tax",
  registrationNumber: "Registration #",
  vatNumber: "VAT number",
  phone: "Phone",
  contactEmail: "Contact email",
  website: "Website",
  currency: "Currency",
  theme: "Theme",
  presets: "Presets",
  brandDescNote: "Platform: RestroPilot · Restaurant: your brand",
  revenueModeSubtitle: "How do we record revenue?",
  revenueDailyDesc: "Daily cash close",
  revenueMonthlyDesc: "Monthly summary",
  revenueCsvDesc: "Bulk import",
  languageAutoNote: "Detected from your browser. Change any time.",

  // ---------- inventory ----------
  inventoryItem: "Inventory item",
  stock: "Stock",
  minStock: "Min stock",
  cost: "Cost",
  value: "Value",
  lowStock: "Low stock",
  criticalStock: "Critical stock",
  outOfStock: "Out of stock",
  reorderAlert: "Reorder alert",
  netCost: "Net cost",
  grossCost: "Gross cost",
  vatRate: "VAT rate",

  // ---------- staff ----------
  role: "Role",
  roleOwner: "Owner",
  roleManager: "Manager",
  roleAccountant: "Accountant",
  roleStaff: "Staff",
  roleKitchen: "Kitchen",
  roleWaiter: "Waiter",
  newStaff: "New staff member",
  activeUsers: "Active users",

  // ---------- reports ----------
  quarterlyVat: "Quarterly VAT",
  outputVat: "Output VAT",
  inputVat: "Input VAT",
  netVat: "Net VAT",
  exportCsv: "Export CSV",

  // ---------- suppliers ----------
  suppliers: "Suppliers",
  newSupplier: "New supplier",
  supplierName: "Supplier name",
  contactPerson: "Contact person",
  paymentTerms: "Payment terms (days)",
  paymentStatus: "Payment status",
  paid: "Paid",
  partial: "Partial",
  unpaid: "Unpaid",

  // ---------- misc / bank tx ----------
  reconciled: "Reconciled",
  unreconciled: "Pending",
  manualTransactions: "Manual transactions",
  recentActivity: "Recent activity",
  auditLog: "Audit log",

  // ---------- errors / toasts ----------
  errorGeneric: "Something went wrong.",
  errorLoadDashboard: "Could not load dashboard data.",
  errorLoadInventory: "Could not load inventory.",
};

const de = {
  appName: "RestroPilot",
  tagline: "Finanzen & Business Intelligence für dein Restaurant",
  brandLine1: "Für dein Restaurant",
  brandLine2: "Finanzen & BI.",
  brandDesc: "Cashflow, Ausgaben, Rechnungen, MwSt. und G&V in einem Panel. EN / DE / TR unterstützt.",

  save: "Speichern",
  cancel: "Abbrechen",
  delete: "Löschen",
  edit: "Bearbeiten",
  add: "Hinzufügen",
  close: "Schließen",
  loading: "Lädt…",
  empty: "Keine Einträge",
  yes: "Ja",
  no: "Nein",
  search: "Suchen…",
  logout: "Abmelden",
  from: "Von",
  to: "Bis",
  date: "Datum",
  amount: "Betrag",
  category: "Kategorie",
  status: "Status",
  notes: "Notizen",
  total: "Gesamt",
  active: "Aktiv",
  actions: "Aktionen",
  retry: "Erneut versuchen",
  confirm: "Bestätigen",
  back: "Zurück",
  next: "Weiter",
  submit: "Absenden",
  create: "Erstellen",
  update: "Aktualisieren",
  refresh: "Aktualisieren",
  export: "Exportieren",
  import: "Importieren",
  print: "Drucken",
  filter: "Filter",
  clear: "Löschen",
  clearAll: "Alles löschen",
  reset: "Zurücksetzen",
  copy: "Kopieren",
  copied: "Kopiert",
  optional: "Optional",
  required: "Pflichtfeld",
  preview: "Vorschau",
  today: "Heute",
  yesterday: "Gestern",
  tomorrow: "Morgen",
  thisWeek: "Diese Woche",
  thisMonth: "Dieser Monat",
  lastMonth: "Letzter Monat",
  last6Months: "Letzte 6 Monate",
  thisYear: "Dieses Jahr",
  all: "Alle",
  none: "Keine",
  ok: "OK",
  areYouSure: "Bist du sicher?",
  deleteConfirm: "Dieser Eintrag wird dauerhaft gelöscht.",
  passwordsMismatch: "Passwörter stimmen nicht überein.",
  requiredField: "Pflichtfeld.",
  minChars: "Mind. {n} Zeichen",
  invalidEmail: "Bitte eine gültige E-Mail eingeben.",
  saved: "Gespeichert",
  saveFailed: "Speichern fehlgeschlagen",
  loadFailed: "Laden fehlgeschlagen",
  networkError: "Netzwerkfehler — bitte erneut versuchen.",

  login: "Anmelden",
  register: "Registrieren",
  email: "E-Mail",
  password: "Passwort",
  passwordAgain: "Passwort wiederholen",
  fullName: "Vor- & Nachname",
  restaurantName: "Restaurantname",
  goToLogin: "Schon ein Konto? Anmelden",
  goToRegister: "Kein Konto? Restaurant erstellen",
  loginHint: "Mit E-Mail und Passwort anmelden",
  forgotPassword: "Passwort vergessen?",
  resetPassword: "Passwort zurücksetzen",
  createRestaurant: "Mein Restaurant erstellen",
  registerHero: "Bring dein Restaurant zu RestroPilot",
  registerHeroDesc: "Erstelle deinen eigenen Tenant — niemand kann auf deine Daten zugreifen.",
  registerSubline: "Erstelle dein Konto und Restaurant in 30 Sekunden.",
  restaurantNameHelper: "Dieser Name erscheint als Titel deines Panels.",
  passwordMinPlaceholder: "Mind. 6 Zeichen",
  passwordConfirmPlaceholder: "Passwort erneut eingeben",

  dashboard: "Dashboard",
  finance: "Finanzen",
  inventory: "Lager",
  staff: "Personal",
  reports: "Berichte",
  settings: "Einstellungen",
  forecast: "Prognose",
  imports: "Import",
  hq: "HQ",
  more: "Mehr",
  copilot: "Copilot",
  orders: "Bestellungen",
  tables: "Tische",
  menu: "Menü",
  kds: "KDS",
  admin: "Admin",

  revenueMtd: "Monatsumsatz",
  expensesMtd: "Monatliche Ausgaben",
  profitMtd: "Monatsgewinn",
  marginMtd: "Gewinnmarge",
  cashOnHand: "Verfügbare Liquidität",
  apOpen: "Offene Verbindlichkeiten",
  arOpen: "Offene Forderungen",
  revenueVsExpenses: "Umsatz vs Ausgaben",
  expenseBreakdown: "Ausgaben nach Kategorie",
  welcome: "Willkommen",
  profitMargin: "Gewinnmarge",
  revenueTrend: "Umsatz-Trend",
  upcomingPayments: "Anstehende Zahlungen",
  daysToDue: "Tage bis fällig",
  todaysPriorities: "Heutige Prioritäten",
  businessHealth: "Unternehmensgesundheit",
  runway: "Cash-Reichweite",
  aiInsight: "KI-Analyse",
  aiInsightHint: "Tägliche automatische Analyse deines Betriebs.",
  regenerate: "Neu generieren",
  lastUpdated: "Zuletzt aktualisiert",
  hoursAgo: "Vor {n} Std.",

  overview: "Übersicht",
  cashFlow: "Cashflow",
  expenses: "Ausgaben",
  invoices: "Rechnungen",
  taxes: "Steuern",
  profitLoss: "G & V",
  bankAccounts: "Bankkonten",
  revenue: "Umsatz",

  newExpense: "Neue Ausgabe",
  vendor: "Lieferant",
  bankAccount: "Konto",
  taxAmount: "MwSt.-Betrag",
  description: "Beschreibung",
  recurring: "Wiederkehrend",
  recurringPeriod: "Rhythmus",
  weekly: "Wöchentlich",
  monthly: "Monatlich",
  yearly: "Jährlich",

  newInvoice: "Neue Rechnung",
  invoiceNumber: "Rechnungs-Nr.",
  partyName: "Partei",
  issueDate: "Rechnungsdatum",
  dueDate: "Fällig",
  netAmount: "Nettobetrag",
  taxRate: "MwSt.-Satz",
  apInvoices: "Verbindlichkeiten (AP)",
  arInvoices: "Forderungen (AR)",
  markPaid: "Als bezahlt markieren",
  invoiceStatusDraft: "Entwurf",
  invoiceStatusOpen: "Offen",
  invoiceStatusPaid: "Bezahlt",
  invoiceStatusOverdue: "Überfällig",

  newRevenue: "Neuer Umsatzeintrag",
  cash: "Bar",
  card: "Karte",
  online: "Online / Lieferdienst",
  bankTransfer: "Überweisung",
  other: "Sonstiges",
  revenueDaily: "Täglich",
  revenueMonthly: "Monatlich",
  revenueCsv: "CSV / Excel importieren",
  revenueMode: "Umsatz-Erfassungsmodus",

  newAccount: "Neues Konto",
  accountName: "Kontoname",
  iban: "IBAN",
  balance: "Kontostand",
  isCash: "Kassenkonto",

  brand: "Marke",
  brandName: "Markenname",
  logoUrl: "Logo-URL",
  faviconUrl: "Favicon-URL",
  primaryColor: "Primärfarbe",
  language: "Sprache",
  languageEn: "Englisch",
  languageTr: "Türkisch",
  languageDe: "Deutsch",
  restaurantSettings: "Restaurant-Einstellungen",
  address: "Adresse",
  companyInfo: "Unternehmensdaten",
  companyInfoDesc: "Erscheint auf Rechnungen und Berichten",
  contactSection: "Kontakt",
  legalTaxSection: "Rechtlich & Steuer",
  registrationNumber: "Firmenbuch-Nr.",
  vatNumber: "UID-Nummer",
  phone: "Telefon",
  contactEmail: "Kontakt-E-Mail",
  website: "Website",
  currency: "Währung",
  theme: "Theme",
  presets: "Presets",
  brandDescNote: "Plattform: RestroPilot · Restaurant: deine Marke",
  revenueModeSubtitle: "Wie erfassen wir Umsätze?",
  revenueDailyDesc: "Täglicher Kassenschluss",
  revenueMonthlyDesc: "Monatliche Summe",
  revenueCsvDesc: "Bulk-Import",
  languageAutoNote: "Automatisch aus deinem Browser erkannt. Jederzeit änderbar.",

  inventoryItem: "Lagerartikel",
  stock: "Bestand",
  minStock: "Mindestbestand",
  cost: "Kosten",
  value: "Wert",
  lowStock: "Niedriger Bestand",
  criticalStock: "Kritischer Bestand",
  outOfStock: "Nicht auf Lager",
  reorderAlert: "Nachbestell-Warnung",
  netCost: "Nettokosten",
  grossCost: "Bruttokosten",
  vatRate: "MwSt.-Satz",

  role: "Rolle",
  roleOwner: "Inhaber",
  roleManager: "Manager",
  roleAccountant: "Buchhalter",
  roleStaff: "Personal",
  roleKitchen: "Küche",
  roleWaiter: "Kellner",
  newStaff: "Neuer Mitarbeiter",
  activeUsers: "Aktive Nutzer",

  quarterlyVat: "Quartals-MwSt.",
  outputVat: "Umsatzsteuer",
  inputVat: "Vorsteuer",
  netVat: "Zahllast",
  exportCsv: "CSV exportieren",

  suppliers: "Lieferanten",
  newSupplier: "Neuer Lieferant",
  supplierName: "Lieferantenname",
  contactPerson: "Ansprechpartner",
  paymentTerms: "Zahlungsziel (Tage)",
  paymentStatus: "Zahlungsstatus",
  paid: "Bezahlt",
  partial: "Teilweise",
  unpaid: "Offen",

  reconciled: "Abgeglichen",
  unreconciled: "Offen",
  manualTransactions: "Manuelle Buchungen",
  recentActivity: "Letzte Aktivitäten",
  auditLog: "Prüfprotokoll",

  errorGeneric: "Etwas ist schiefgelaufen.",
  errorLoadDashboard: "Dashboard-Daten konnten nicht geladen werden.",
  errorLoadInventory: "Lagerdaten konnten nicht geladen werden.",
};

const tr = {
  appName: "RestroPilot",
  tagline: "Restoranın için finans & iş zekası",
  brandLine1: "Restoranın için",
  brandLine2: "finans & iş zekası.",
  brandDesc: "Nakit akışı, giderler, faturalar, KDV ve kâr/zarar tek panelde. EN / DE / TR destekli.",

  save: "Kaydet",
  cancel: "İptal",
  delete: "Sil",
  edit: "Düzenle",
  add: "Ekle",
  close: "Kapat",
  loading: "Yükleniyor…",
  empty: "Kayıt yok",
  yes: "Evet",
  no: "Hayır",
  search: "Ara…",
  logout: "Çıkış yap",
  from: "Başlangıç",
  to: "Bitiş",
  date: "Tarih",
  amount: "Tutar",
  category: "Kategori",
  status: "Durum",
  notes: "Notlar",
  total: "Toplam",
  active: "Aktif",
  actions: "İşlemler",
  retry: "Tekrar dene",
  confirm: "Onayla",
  back: "Geri",
  next: "İleri",
  submit: "Gönder",
  create: "Oluştur",
  update: "Güncelle",
  refresh: "Yenile",
  export: "Dışa aktar",
  import: "İçe aktar",
  print: "Yazdır",
  filter: "Filtrele",
  clear: "Temizle",
  clearAll: "Tümünü temizle",
  reset: "Sıfırla",
  copy: "Kopyala",
  copied: "Kopyalandı",
  optional: "İsteğe bağlı",
  required: "Zorunlu",
  preview: "Önizleme",
  today: "Bugün",
  yesterday: "Dün",
  tomorrow: "Yarın",
  thisWeek: "Bu hafta",
  thisMonth: "Bu ay",
  lastMonth: "Geçen ay",
  last6Months: "Son 6 ay",
  thisYear: "Bu yıl",
  all: "Tümü",
  none: "Yok",
  ok: "Tamam",
  areYouSure: "Emin misin?",
  deleteConfirm: "Bu kayıt kalıcı olarak silinecek.",
  passwordsMismatch: "Şifreler eşleşmiyor.",
  requiredField: "Bu alan zorunludur.",
  minChars: "En az {n} karakter",
  invalidEmail: "Geçerli bir e-posta gir.",
  saved: "Kaydedildi",
  saveFailed: "Kaydetme başarısız",
  loadFailed: "Yükleme başarısız",
  networkError: "Ağ hatası — lütfen tekrar deneyin.",

  login: "Giriş yap",
  register: "Hesap oluştur",
  email: "E-posta",
  password: "Şifre",
  passwordAgain: "Şifreyi tekrar gir",
  fullName: "Ad Soyad",
  restaurantName: "Restoran adı",
  goToLogin: "Zaten hesabın var mı? Giriş yap",
  goToRegister: "Hesabın yok mu? Restoranını oluştur",
  loginHint: "E-posta ve şifrenle giriş yap",
  forgotPassword: "Şifremi unuttum",
  resetPassword: "Şifreyi sıfırla",
  createRestaurant: "Restoranımı Oluştur",
  registerHero: "Restoranını RestroPilot'a taşı",
  registerHeroDesc: "Kendi tenant'ını oluştur — kimse verilerine erişemez.",
  registerSubline: "Aşağıdaki bilgilerle hesabını ve restoranını 30 saniyede kur.",
  restaurantNameHelper: "Bu isim panelinin başlığı ve mutfağın adı olarak görünür.",
  passwordMinPlaceholder: "En az 6 karakter",
  passwordConfirmPlaceholder: "Şifreni tekrar yaz",

  dashboard: "Panel",
  finance: "Finans",
  inventory: "Stok",
  staff: "Personel",
  reports: "Raporlar",
  settings: "Ayarlar",
  forecast: "Tahmin",
  imports: "İçe Aktar",
  hq: "HQ",
  more: "Diğer",
  copilot: "Copilot",
  orders: "Siparişler",
  tables: "Masalar",
  menu: "Menü",
  kds: "KDS",
  admin: "Yönetim",

  revenueMtd: "Aylık Ciro",
  expensesMtd: "Aylık Gider",
  profitMtd: "Aylık Kâr",
  marginMtd: "Kâr Marjı",
  cashOnHand: "Nakit Mevcudu",
  apOpen: "Ödenecek Faturalar",
  arOpen: "Tahsil Edilecek",
  revenueVsExpenses: "Ciro vs Gider",
  expenseBreakdown: "Gider Dağılımı",
  welcome: "Hoş geldin",
  profitMargin: "Kâr Marjı",
  revenueTrend: "Ciro Trendi",
  upcomingPayments: "Yaklaşan Ödemeler",
  daysToDue: "Vadeye kalan",
  todaysPriorities: "Bugünün öncelikleri",
  businessHealth: "İşletme sağlığı",
  runway: "Nakit süresi",
  aiInsight: "Yapay zeka analizi",
  aiInsightHint: "Operasyonunun günlük otomatik analizi.",
  regenerate: "Yeniden üret",
  lastUpdated: "Son güncelleme",
  hoursAgo: "{n} saat önce",

  overview: "Genel Bakış",
  cashFlow: "Nakit Akışı",
  expenses: "Giderler",
  invoices: "Faturalar",
  taxes: "Vergiler",
  profitLoss: "Kâr / Zarar",
  bankAccounts: "Banka Hesapları",
  revenue: "Ciro",

  newExpense: "Yeni Gider",
  vendor: "Tedarikçi",
  bankAccount: "Hesap",
  taxAmount: "KDV Tutarı",
  description: "Açıklama",
  recurring: "Tekrarlayan",
  recurringPeriod: "Tekrar dönemi",
  weekly: "Haftalık",
  monthly: "Aylık",
  yearly: "Yıllık",

  newInvoice: "Yeni Fatura",
  invoiceNumber: "Fatura No",
  partyName: "Taraf",
  issueDate: "Düzenleme Tarihi",
  dueDate: "Vade",
  netAmount: "Net Tutar",
  taxRate: "KDV Oranı",
  apInvoices: "Ödenecek (AP)",
  arInvoices: "Tahsil Edilecek (AR)",
  markPaid: "Ödendi işaretle",
  invoiceStatusDraft: "Taslak",
  invoiceStatusOpen: "Açık",
  invoiceStatusPaid: "Ödendi",
  invoiceStatusOverdue: "Vadesi geçti",

  newRevenue: "Yeni Ciro Girişi",
  cash: "Nakit",
  card: "Kart",
  online: "Online / Yemek uygulaması",
  bankTransfer: "Havale",
  other: "Diğer",
  revenueDaily: "Günlük",
  revenueMonthly: "Aylık",
  revenueCsv: "CSV / Excel içe aktar",
  revenueMode: "Ciro Takip Yöntemi",

  newAccount: "Yeni Hesap",
  accountName: "Hesap Adı",
  iban: "IBAN",
  balance: "Bakiye",
  isCash: "Kasa hesabı",

  brand: "Marka",
  brandName: "Marka adı",
  logoUrl: "Logo URL",
  faviconUrl: "Favicon URL",
  primaryColor: "Ana renk",
  language: "Dil",
  languageEn: "İngilizce",
  languageTr: "Türkçe",
  languageDe: "Almanca",
  restaurantSettings: "Restoran Ayarları",
  address: "Adres",
  companyInfo: "Şirket Bilgileri",
  companyInfoDesc: "Faturalarda ve raporlarda gösterilir",
  contactSection: "İletişim",
  legalTaxSection: "Yasal & vergi",
  registrationNumber: "Vergi/Ticaret No",
  vatNumber: "KDV No",
  phone: "Telefon",
  contactEmail: "İletişim e-posta",
  website: "Web sitesi",
  currency: "Para birimi",
  theme: "Tema",
  presets: "Hazır renkler",
  brandDescNote: "Platform: RestroPilot · Restoran: senin markan",
  revenueModeSubtitle: "Ciro takip yöntemini seç",
  revenueDailyDesc: "Her gün kasa kapanış",
  revenueMonthlyDesc: "Aylık toplam giriş",
  revenueCsvDesc: "Toplu içe aktarım",
  languageAutoNote: "Tarayıcından algılandı. İstediğin zaman değiştir.",

  inventoryItem: "Stok kalemi",
  stock: "Stok",
  minStock: "Min stok",
  cost: "Maliyet",
  value: "Değer",
  lowStock: "Düşük stok",
  criticalStock: "Kritik stok",
  outOfStock: "Stokta yok",
  reorderAlert: "Sipariş uyarısı",
  netCost: "Net maliyet",
  grossCost: "Brüt maliyet",
  vatRate: "KDV oranı",

  role: "Rol",
  roleOwner: "Sahip",
  roleManager: "Yönetici",
  roleAccountant: "Muhasebeci",
  roleStaff: "Personel",
  roleKitchen: "Mutfak",
  roleWaiter: "Garson",
  newStaff: "Yeni personel",
  activeUsers: "Aktif kullanıcılar",

  quarterlyVat: "Üç Aylık KDV",
  outputVat: "Toplanan KDV",
  inputVat: "İndirilecek KDV",
  netVat: "Ödenecek KDV",
  exportCsv: "CSV dışa aktar",

  suppliers: "Tedarikçiler",
  newSupplier: "Yeni Tedarikçi",
  supplierName: "Tedarikçi adı",
  contactPerson: "Yetkili kişi",
  paymentTerms: "Ödeme vadesi (gün)",
  paymentStatus: "Ödeme durumu",
  paid: "Ödendi",
  partial: "Kısmi",
  unpaid: "Ödenmedi",

  reconciled: "Mutabık",
  unreconciled: "Beklemede",
  manualTransactions: "Manuel Hareketler",
  recentActivity: "Son Aktiviteler",
  auditLog: "Denetim Kaydı",

  errorGeneric: "Bir şeyler ters gitti.",
  errorLoadDashboard: "Panel verileri yüklenemedi.",
  errorLoadInventory: "Stok verileri yüklenemedi.",
};

export const dict = { en, de, tr };
